import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import {
  getBalanceAndPin,
  savePersonalDetailsAction
} from "../../features/balance-and-pin/balanceAndPinSlice";
import usePrevious from "../../hooks/usePrevious";
import { formattedBalance } from "../../features/balance-and-pin/utils";
import { getDeviceInfo } from "../../features/hugo-boss/hugoBossSlice";
import Spinner from "../../components/Spinner";
import RugbyWc25Form from "./RugbyWc25Form";

import {
  MainContainer,
  Header,
  LogoSection,
  Logo,
  BalanceInfo,
  HeroImage,
  ContentSection,
  Title,
  Description,
  BlackSection,
  BlackTitle,
  BlackDescription,
  Footer,
  PoweredBySection,
  PoweredByText,
  LogoContainer,
  FooterLogo,
  LinksSection,
  LinkText,
  Alert,
  PinAlert
} from "./styles";

import womensRugbyDefenderLogo from "./images/womens-rugby-defender-logo.png";
import defenderVehicleHero from "./images/defender-vehicle-hero.png";
import digiseqLogo from "./images/digiseq-logo.png";

const RugbyWc25 = () => {
  const [searchParams] = useSearchParams();
  const serialNumber = searchParams.get("serialNumber");
  const dispatch = useDispatch();

  const [showForm, setShowForm] = useState(false);
  const [showPinAlert, setShowPinAlert] = useState(false);
  const [localPersonalDetails, setLocalPersonalDetails] = useState(null);

  const {
    balanceAndPin,
    isLoading,
    savingPersonalDetails,
    errorSavingPersonalDetails
  } = useSelector((state) => state.balanceAndPin);

  const { device, gettingDeviceInfo } = useSelector((state) => state.hugoBoss);

  const prevSavingPersonalDetails = usePrevious(savingPersonalDetails);

  useEffect(() => {
    if (!balanceAndPin && serialNumber) {
      dispatch(
        getBalanceAndPin({
          serial: serialNumber,
          query: "?provider=pannovate"
        })
      );
    }
  }, [balanceAndPin, serialNumber, dispatch]);

  useEffect(() => {
    dispatch(getDeviceInfo(serialNumber));
  }, [dispatch, serialNumber]);

  useEffect(() => {
    if (
      prevSavingPersonalDetails &&
      !savingPersonalDetails &&
      !errorSavingPersonalDetails &&
      localPersonalDetails
    ) {
      setShowPinAlert(true);
    }
  }, [
    prevSavingPersonalDetails,
    savingPersonalDetails,
    errorSavingPersonalDetails,
    localPersonalDetails
  ]);

  const handleViewPin = () => {
    if (
      (device?.personalDetails?.fullName &&
        device?.personalDetails?.phoneNumber) ||
      (localPersonalDetails?.fullName && localPersonalDetails?.phoneNumber)
    ) {
      setShowPinAlert(true);
      setShowForm(false);
    } else {
      setShowForm(true);
      setShowPinAlert(false);
    }
  };

  const handleFormSubmit = (formData) => {
    setLocalPersonalDetails(formData);
    dispatch(
      savePersonalDetailsAction({
        serial: serialNumber,
        details: {
          fullName: formData.fullName,
          phoneNumber: formData.phoneNumber
        }
      })
    );
    setShowForm(false);
  };

  const handleFormCancel = () => {
    setShowForm(false);
  };

  const handleClosePinAlert = () => {
    setShowPinAlert(false);
  };

  if (isLoading || gettingDeviceInfo) return <Spinner />;

  return (
    <MainContainer>
      <Header>
        <LogoSection>
          <Logo src={womensRugbyDefenderLogo} />
        </LogoSection>
        <BalanceInfo>
          <div className="balance-amount">
            {formattedBalance(
              balanceAndPin?.balanceDetails?.availableBalance,
              balanceAndPin?.balanceDetails?.currency
            )}
          </div>
          <div
            className="balance-label"
            onClick={handleViewPin}
            style={{ cursor: "pointer" }}
          >
            View PIN
          </div>
        </BalanceInfo>
      </Header>

      <HeroImage src={defenderVehicleHero} />

      <ContentSection>
        <Title>
          Defender,
          <br />
          Principal Partner of Women's Rugby World Cup 2025!
        </Title>
        <Description>
          Driven by a determined purpose, the partnership represents a
          celebration of female strength, spirit and capability from grassroots
          through to the elite levels of the sport. There will be 64 Defender
          Mascots stepping onto the pitch with team captains throughout the
          tournament.
        </Description>
      </ContentSection>

      <BlackSection>
        <BlackTitle>Enjoy the Match!</BlackTitle>
        <BlackDescription>
          Having issues? Please get in touch with your Defender chaperone.
        </BlackDescription>
      </BlackSection>

      <Footer>
        <PoweredBySection>
          <PoweredByText>Powered by</PoweredByText>
          <LogoContainer>
            <FooterLogo src={digiseqLogo} />
          </LogoContainer>
        </PoweredBySection>

        <LinksSection>
          <LinkText
            href="https://www.bobofin.com/privacy-policy-cookies-policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </LinkText>
          <LinkText
            href="https://www.bobofin.com/terms-and-conditions"
            target="_blank"
            rel="noopener noreferrer"
          >
            Terms and Conditions
          </LinkText>
        </LinksSection>
      </Footer>

      {showForm && (
        <Alert>
          <div className="inner-container">
            <RugbyWc25Form
              savingPersonalDetails={savingPersonalDetails}
              onSubmit={handleFormSubmit}
              onCancel={handleFormCancel}
            />
          </div>
        </Alert>
      )}

      {showPinAlert && (
        <PinAlert>
          <div className="inner-container">
            <h3>Your PIN</h3>
            <div className="pin-display">
              {balanceAndPin?.pinDetails?.pin || "N/A"}
            </div>
            <button onClick={handleClosePinAlert}>Close</button>
          </div>
        </PinAlert>
      )}
    </MainContainer>
  );
};

export default RugbyWc25;
